import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  ArrowLeft, 
  Star, 
  Heart, 
  Share2, 
  CheckCircle, 
  Truck, 
  Shield, 
  Phone,
  ChevronLeft,
  ChevronRight,
  Zap,
  Award
} from 'lucide-react';
import { getProductById, products } from '../data/products';

const ProductDetailPage = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedTab, setSelectedTab] = useState('details');
  const [quantity, setQuantity] = useState(1);
  const [isWishlisted, setIsWishlisted] = useState(false);

  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  useEffect(() => {
    const foundProduct = getProductById(id);
    setProduct(foundProduct);
  }, [id]);

  if (!product) {
    return (
      <div className="min-h-screen bg-dark-950 pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-dark-300">Loading product details...</p>
        </div>
      </div>
    );
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === product.images.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? product.images.length - 1 : prev - 1
    );
  };

  const relatedProducts = products
    .filter(p => p.category === product.category && p.id !== product.id)
    .slice(0, 4);

  const benefits = [
    {
      icon: Truck,
      title: "Free Shipping",
      description: "White-glove delivery included"
    },
    {
      icon: Shield,
      title: `${product.specifications?.warranty || '3 Years'} Warranty`,
      description: "Comprehensive coverage included"
    },
    {
      icon: Zap,
      title: "Expert Installation",
      description: "Professional setup available"
    },
    {
      icon: Award,
      title: "Handcrafted Quality",
      description: "Made by master artisans"
    }
  ];

  const tabs = [
    { id: 'details', label: 'Details' },
    { id: 'specifications', label: 'Specifications' },
    { id: 'care', label: 'Care Guide' },
    { id: 'reviews', label: `Reviews (${product.reviews || 0})` }
  ];

  return (
    <div className="min-h-screen bg-dark-950 pt-20">
      {/* Breadcrumb */}
      <div className="container mx-auto px-4 max-w-6xl py-6">
        <Link 
          to="/products" 
          className="inline-flex items-center text-accent-400 hover:text-accent-300 transition-colors duration-300"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Products
        </Link>
      </div>

      {/* Product Section */}
      <section className="py-8">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            ref={ref}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            variants={containerVariants}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12"
          >
            {/* Image Gallery */}
            <motion.div variants={itemVariants} className="space-y-4">
              {/* Main Image */}
              <div className="relative aspect-square rounded-2xl overflow-hidden bg-dark-800">
                <img
                  src={product.images[currentImageIndex]}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
                
                {/* Navigation Arrows */}
                {product.images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-black/70 transition-colors duration-300"
                    >
                      <ChevronLeft className="h-6 w-6 text-white" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-black/70 transition-colors duration-300"
                    >
                      <ChevronRight className="h-6 w-6 text-white" />
                    </button>
                  </>
                )}

                {/* Sale Badge */}
                {product.originalPrice > product.price && (
                  <div className="absolute top-4 left-4">
                    <span className="bg-accent-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                      Sale
                    </span>
                  </div>
                )}
              </div>

              {/* Thumbnail Gallery */}
              {product.images.length > 1 && (
                <div className="grid grid-cols-4 gap-4">
                  {product.images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`aspect-square rounded-lg overflow-hidden border-2 transition-all duration-300 ${
                        index === currentImageIndex
                          ? 'border-accent-500'
                          : 'border-transparent hover:border-dark-600'
                      }`}
                    >
                      <img
                        src={image}
                        alt={`${product.name} view ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Product Info */}
            <motion.div variants={itemVariants} className="space-y-6">
              {/* Header */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-accent-400 text-sm font-medium uppercase tracking-wide">
                    {product.category}
                  </span>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setIsWishlisted(!isWishlisted)}
                      className={`p-2 rounded-full transition-colors duration-300 ${
                        isWishlisted 
                          ? 'bg-accent-500 text-white' 
                          : 'bg-dark-800 text-dark-400 hover:text-accent-400'
                      }`}
                    >
                      <Heart className="h-5 w-5" />
                    </button>
                    <button className="p-2 bg-dark-800 text-dark-400 hover:text-accent-400 rounded-full transition-colors duration-300">
                      <Share2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  {product.name}
                </h1>

                <div className="flex items-center space-x-4 mb-4">
                  <div className="flex items-center space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        className={`h-5 w-5 ${
                          i < Math.floor(product.rating) 
                            ? 'text-yellow-400 fill-current' 
                            : 'text-dark-400'
                        }`} 
                      />
                    ))}
                    <span className="text-dark-300 ml-2">
                      {product.rating} ({product.reviews} reviews)
                    </span>
                  </div>
                </div>

                <p className="text-lg text-dark-300 leading-relaxed mb-6">
                  {product.longDescription || product.description}
                </p>
              </div>

              {/* Pricing */}
              <div className="border border-dark-700 rounded-2xl p-6 bg-dark-800/30">
                <div className="flex items-center space-x-4 mb-4">
                  <span className="text-3xl font-bold text-white">
                    ${product.price.toLocaleString()}
                  </span>
                  {product.originalPrice > product.price && (
                    <span className="text-xl text-dark-400 line-through">
                      ${product.originalPrice.toLocaleString()}
                    </span>
                  )}
                  {product.originalPrice > product.price && (
                    <span className="bg-accent-500 text-white px-2 py-1 rounded text-sm font-medium">
                      Save ${(product.originalPrice - product.price).toLocaleString()}
                    </span>
                  )}
                </div>

                {/* Stock Status */}
                <div className="flex items-center space-x-2 mb-6">
                  <CheckCircle className={`h-5 w-5 ${product.inStock ? 'text-green-500' : 'text-red-500'}`} />
                  <span className={`font-medium ${product.inStock ? 'text-green-400' : 'text-red-400'}`}>
                    {product.inStock ? 'In Stock' : 'Currently Unavailable'}
                  </span>
                </div>

                {/* Quantity and Add to Cart */}
                {product.inStock && (
                  <div className="space-y-4">
                    <div className="flex items-center space-x-4">
                      <label className="text-white font-medium">Quantity:</label>
                      <div className="flex items-center border border-dark-600 rounded-lg">
                        <button
                          onClick={() => quantity > 1 && setQuantity(quantity - 1)}
                          className="p-2 hover:bg-dark-700 transition-colors duration-300"
                        >
                          -
                        </button>
                        <span className="px-4 py-2 text-white">{quantity}</span>
                        <button
                          onClick={() => setQuantity(quantity + 1)}
                          className="p-2 hover:bg-dark-700 transition-colors duration-300"
                        >
                          +
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <button className="bg-gradient-to-r from-accent-500 to-accent-600 text-white py-3 px-6 rounded-lg font-medium hover:from-accent-600 hover:to-accent-700 transition-all duration-300">
                        Add to Cart
                      </button>
                      <Link 
                        to="/contact"
                        className="border border-accent-500 text-accent-400 py-3 px-6 rounded-lg font-medium hover:bg-accent-500/10 transition-all duration-300 text-center"
                      >
                        <Phone className="h-4 w-4 inline mr-2" />
                        Get Quote
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              {/* Benefits */}
              <div className="grid grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3 p-4 bg-dark-800/30 rounded-xl">
                    <benefit.icon className="h-6 w-6 text-accent-400 mt-1" />
                    <div>
                      <div className="text-white font-medium text-sm">{benefit.title}</div>
                      <div className="text-dark-400 text-xs">{benefit.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Product Details Tabs */}
      <section className="py-16 bg-dark-800">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            {/* Tab Navigation */}
            <motion.div variants={itemVariants} className="border-b border-dark-700 mb-8">
              <div className="flex space-x-8">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setSelectedTab(tab.id)}
                    className={`py-4 px-2 font-medium transition-colors duration-300 border-b-2 ${
                      selectedTab === tab.id
                        ? 'text-accent-400 border-accent-400'
                        : 'text-dark-300 border-transparent hover:text-white'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </motion.div>

            {/* Tab Content */}
            <motion.div variants={itemVariants}>
              {selectedTab === 'details' && (
                <div className="space-y-6">
                  <h3 className="text-2xl font-bold text-white mb-4">Product Details</h3>
                  <p className="text-dark-300 leading-relaxed text-lg mb-6">
                    {product.longDescription || product.description}
                  </p>
                  
                  {product.features && (
                    <div>
                      <h4 className="text-xl font-semibold text-white mb-4">Key Features</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {product.features.map((feature, index) => (
                          <div key={index} className="flex items-center space-x-3">
                            <CheckCircle className="h-5 w-5 text-accent-400" />
                            <span className="text-dark-300">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {selectedTab === 'specifications' && product.specifications && (
                <div>
                  <h3 className="text-2xl font-bold text-white mb-6">Specifications</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {Object.entries(product.specifications).map(([key, value], index) => (
                      <div key={index} className="flex justify-between items-center py-3 border-b border-dark-700">
                        <span className="text-dark-300 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                        <span className="text-white font-medium">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedTab === 'care' && (
                <div>
                  <h3 className="text-2xl font-bold text-white mb-6">Care Instructions</h3>
                  <div className="space-y-4 text-dark-300 leading-relaxed">
                    <p>Proper care ensures your ResinCraft Studio furniture maintains its beauty for generations:</p>
                    <ul className="space-y-2 list-disc list-inside">
                      <li>Clean regularly with a soft, damp cloth</li>
                      <li>Use coasters to prevent water rings and heat damage</li>
                      <li>Avoid harsh chemicals and abrasive cleaners</li>
                      <li>Keep away from direct sunlight to prevent fading</li>
                      <li>Professional refinishing available every 5-7 years</li>
                    </ul>
                  </div>
                </div>
              )}

              {selectedTab === 'reviews' && (
                <div>
                  <h3 className="text-2xl font-bold text-white mb-6">Customer Reviews</h3>
                  <div className="text-center py-12 text-dark-300">
                    <Star className="h-16 w-16 mx-auto mb-4 text-dark-600" />
                    <p>Reviews coming soon...</p>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-6xl">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={containerVariants}
            >
              <motion.div variants={itemVariants} className="text-center mb-12">
                <h2 className="text-3xl font-bold text-white mb-4">
                  Related <span className="bg-gradient-to-r from-accent-400 to-accent-600 bg-clip-text text-transparent">Products</span>
                </h2>
                <p className="text-dark-300">
                  Discover more pieces from our {product.category} collection
                </p>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {relatedProducts.map((relatedProduct, index) => (
                  <motion.div
                    key={relatedProduct.id}
                    variants={itemVariants}
                    className="group"
                  >
                    <Link
                      to={`/products/${relatedProduct.id}`}
                      className="block bg-dark-800/30 backdrop-blur-md rounded-2xl overflow-hidden border border-dark-700 hover:border-accent-500/30 transition-all duration-500 hover:scale-105"
                    >
                      <div className="aspect-square overflow-hidden">
                        <img
                          src={relatedProduct.image}
                          alt={relatedProduct.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-accent-400 transition-colors duration-300">
                          {relatedProduct.name}
                        </h3>
                        <div className="flex items-center justify-between">
                          <span className="text-accent-400 font-bold">
                            ${relatedProduct.price.toLocaleString()}
                          </span>
                          <div className="flex items-center space-x-1">
                            <Star className="h-4 w-4 text-yellow-400 fill-current" />
                            <span className="text-sm text-dark-300">{relatedProduct.rating}</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ProductDetailPage;